import { AppShell } from '@mantine/core'
import { useAuthorization } from '@opsopti/shared'
import {
  IconFolder,
  IconMapPin,
  IconPackage,
  IconScan,
  IconSunglasses,
  IconUsers,
  IconViewfinder,
} from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import type { PropsWithChildren } from 'react'
import { useMemo } from 'react'

import { GlobalLoadingOverlay } from '@/components/GlobalLoadingOverlay'
import { ForbiddenScreen } from '@/screens/403Screen'

import { Navbar } from './Navbar'
import type { LinksGroup } from './NavbarLinksGroup'
import { NavbarLinksGroup } from './NavbarLinksGroup'

type DashboardLayoutProps = PropsWithChildren

export const DashboardLayout = observer(function DashboardLayout(props: DashboardLayoutProps) {
  const links = useMemo(() => staticLinks.map(item => <NavbarLinksGroup {...item} key={item.label} />), [])

  const isMember = useAuthorization({ UserIsMember: true })

  if (isMember) {
    return (
      <ForbiddenScreen
        reason="You must be a Supervisor or Admin to use the web application. You can log out, or for all other user types, please download the mobile app instead."
        showAppLinks
      />
    )
  }

  return (
    <>
      <AppShell
        navbar={{
          width: 280,
          breakpoint: 'sm',
          collapsed: { mobile: true },
        }}
        // aside={{
        //   width: 320,
        //   breakpoint: 'sm',
        //   collapsed: { mobile: true },
        // }}
      >
        <AppShell.Navbar>
          <Navbar>{links}</Navbar>{' '}
        </AppShell.Navbar>

        <AppShell.Main bg="gray.1">{props.children}</AppShell.Main>

        <GlobalLoadingOverlay />
      </AppShell>
    </>
  )
})

const staticLinks: LinksGroup[] = [
  // { label: 'Dashboard', icon: IconTable, to: { name: 'dashboard' } },
  { label: 'Users', icon: IconUsers, to: { name: 'users' } },
  {
    label: 'Asset Sentry',
    to: { name: 'assetSentry' },
    icon: IconSunglasses,
    opened: true,
    additionalCurrentRoutes: [
      'assetSentryAssets',
      'assetSentryAssetNew',
      'assetSentryAssetEdit',
      'assetSentryGroups',
      'assetSentryGroupNew',
      'assetSentryGroupEdit',
      'assetSentryTrackers',
      'assetSentryTrackerEdit',
      'assetSentryTrackerNew',
      'assetSentryTargets',
      'assetSentryTargetEdit',
      'assetSentryTargetNew',
      'assetSentryAssignments',
      'assetSentryAssignmentEdit',
      'assetSentryAssignmentNew',
    ],
    links: [
      {
        label: 'All Assets',
        icon: IconPackage,
        to: { name: 'assetSentryAssets' },
        additionalCurrentRoutes: ['assetSentryAssetEdit', 'assetSentryAssetNew'],
      },
      {
        label: 'Grouped Assets',
        icon: IconFolder,
        to: { name: 'assetSentryGroups' },
        additionalCurrentRoutes: ['assetSentryGroupEdit', 'assetSentryGroupNew'],
      },
      {
        label: 'Trackers',
        icon: IconScan,
        to: { name: 'assetSentryTrackers' },
        additionalCurrentRoutes: ['assetSentryTrackerEdit', 'assetSentryTrackerNew'],
      },
      {
        label: 'Assignment Targets',
        icon: IconViewfinder,
        to: { name: 'assetSentryTargets' },
        additionalCurrentRoutes: ['assetSentryTargetEdit', 'assetSentryTargetNew'],
      },
      {
        label: 'Activity',
        icon: IconMapPin,
        to: { name: 'assetSentryAssignments' },
        additionalCurrentRoutes: ['assetSentryAssignmentEdit', 'assetSentryAssignmentNew'],
      },
    ],
  },
]
